import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Upload, FileText, Download, Trash2, Loader2, Paperclip } from 'lucide-react';
import { authedUrl } from '../../../lib/authSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const UploadedDocumentsPanel = ({ caseId, agent }) => {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/compliance/cases/${caseId}/uploaded-documents`);
      if (r.ok) setItems(await r.json());
    } catch (e) { console.error(e); }
  }, [caseId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (label.trim()) fd.append('label', label.trim());
      fd.append('uploaded_by_id', agent?.id || '');
      fd.append('uploaded_by_name', agent?.name || 'Advisor');
      const r = await fetch(`${API_URL}/api/compliance/cases/${caseId}/uploaded-documents`, {
        method: 'POST',
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const fallback = r.status === 413
          ? 'File is too large (max 25 MB).'
          : r.status === 400
            ? 'Only PDF, JPG, or PNG files are accepted (max 25 MB).'
            : 'Upload failed. Please try again.';
        throw new Error(err.detail || fallback);
      }
      setLabel('');
      if (fileRef.current) fileRef.current.value = '';
      fetchItems();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const download = (id) => {
    window.open(authedUrl(`${API_URL}/api/compliance/uploaded-documents/${id}/download`), '_blank', 'noopener');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this uploaded document? This cannot be undone.')) return;
    await fetch(
      `${API_URL}/api/compliance/uploaded-documents/${id}?advisor_id=${encodeURIComponent(agent?.id || '')}&advisor_name=${encodeURIComponent(agent?.name || 'Advisor')}`,
      { method: 'DELETE' },
    );
    fetchItems();
  };

  return (
    <Card data-testid="uploaded-documents-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-[#003D5C]" />Uploaded Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          Upload signed paper documents or scans onto this file. Useful when a client signs on paper —
          download the generated PDF, have the client sign it, then scan and upload it here. Accepts PDF, JPG, or PNG (max 25&nbsp;MB).
        </p>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end border rounded-lg p-3 bg-gray-50">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Signed Advisor Disclosure"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#003D5C]"
              data-testid="uploaded-doc-label-input"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            data-testid="uploaded-doc-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]"
            data-testid="uploaded-doc-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            {uploading ? 'Uploading…' : 'Choose & Upload'}
          </Button>
        </div>

        {error && (
          <div className="border border-red-300 bg-red-50 text-red-900 rounded-lg p-2 text-sm" data-testid="uploaded-doc-error">
            ⚠ {error}
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-2" data-testid="uploaded-doc-list">
            {items.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 border rounded-lg p-3" data-testid={`uploaded-doc-${doc.id}`}>
                <FileText className="h-5 w-5 text-[#003D5C] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{doc.label || doc.original_filename}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {doc.original_filename} · {fmtSize(doc.size)}
                    {doc.uploaded_by_name ? ` · by ${doc.uploaded_by_name}` : ''}
                    {doc.created_at ? ` · ${new Date(doc.created_at).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => download(doc.id)} data-testid={`download-uploaded-doc-${doc.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(doc.id)} data-testid={`delete-uploaded-doc-${doc.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UploadedDocumentsPanel;
